/**
 * <LinkAttachment> — the external-link preview card (molecule, L0.1).
 *
 * One of the three focused attachment previews (with `<ImageAttachment>` / `<PDFAttachment>`). A tinted
 * `<Card>` with a title and a single row: a link glyph, the link title (or raw url), and a trash
 * affordance. Tapping the card fires `onOpen` (open the viewer). No network — the rich Open-Graph preview
 * stays app-side; the caller may pass a resolved `title`.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Card } from "../Card"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Icon } from "../../atoms/icons/Icon"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"

export type LinkAttachmentProps = {
    url: string
    /** Resolved link title (falls back to the raw url). */
    title?: string
    onDelete: () => void
    onOpen?: () => void
    testID?: string
}

export function LinkAttachment(props: LinkAttachmentProps) {
    const { url, title, onDelete, onOpen, testID } = props

    return (
        <Card background="primary-soft" p="5" pressable={onOpen != null} onPress={onOpen} testID={testID}>
            <Stack gap="3">
                <Text size="text-lg" weight="bold" color="primary">
                    Lien externe
                </Text>
                <Row gap="2" align="center">
                    <Icon name="link" color="primary" />
                    <Text numberOfLines={1} style={styles.grow}>
                        {title ?? url}
                    </Text>
                    <IconButton
                        iconName="trash"
                        color="danger"
                        size={28}
                        iconSize={13}
                        onPress={onDelete}
                        accessibilityLabel="Supprimer"
                    />
                </Row>
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    grow: {
        flex: 1,
    },
}))
